/**
 * SVG path-data → Canvas2D replay (WI #5531, PDF export).
 *
 * The scene painters draw activity hexagons and phase ribbons by passing a
 * `Path2D` to `ctx.fill()` / `ctx.stroke()`. The `svgcanvas` shim used for the
 * vector PDF export implements the path-building API (moveTo/bezierCurveTo/…) but
 * its `fill`/`stroke` take NO argument — it cannot consume a `Path2D`. So for the
 * export ctx we re-trace the original `d` string (stashed on each `Path2D` under
 * `SVG_PATH_DATA` by `draw/shapes.ts`) through `beginPath()` + the path API.
 *
 * Supports M/L/H/V/C/S/Q/T/A/Z (absolute + relative): the map's own shapes
 * (hexagon, phase ribbons) use cubics; the Lucide state-icon glyphs add
 * elliptical arcs (A → `ctx.ellipse`). An unknown command throws rather than
 * silently dropping, so a new shape can't go missing unnoticed.
 */

type PathTarget = Pick<
  CanvasRenderingContext2D,
  | 'beginPath'
  | 'moveTo'
  | 'lineTo'
  | 'bezierCurveTo'
  | 'quadraticCurveTo'
  | 'ellipse'
  | 'closePath'
>;

/** Tokenise a path-data string into command letters and numeric operands. */
function tokenize(d: string): (string | number)[] {
  // Numbers (incl. signs, decimals, exponents) and single command letters. The
  // global regex naturally splits `12-3` and `1.2.3` the way the SVG spec does.
  const re = /[a-z]|-?\d*\.?\d+(?:e[-+]?\d+)?/gi;
  const tokens: (string | number)[] = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(d)) !== null) {
    const t = m[0];
    // A command is a single letter; a number may CONTAIN a letter (`1.5e-7`), so
    // test the whole token, not just "has a letter".
    tokens.push(/^[a-z]$/i.test(t) ? t : parseFloat(t));
  }

  return tokens;
}

/**
 * Replay an SVG path-data string onto a Canvas2D-style context. Issues a fresh
 * `beginPath()`; the caller fills/strokes afterwards (mirroring how the painters
 * use the original `Path2D`).
 */
export function tracePathData(ctx: PathTarget, d: string): void {
  const tk = tokenize(d);
  let i = 0;

  // Current point, subpath start, and the previous cubic/quadratic control point
  // (for the S/T smooth-curve reflection).
  let cx = 0;
  let cy = 0;
  let startX = 0;
  let startY = 0;
  let prevCubicCtrlX = 0;
  let prevCubicCtrlY = 0;
  let prevQuadCtrlX = 0;
  let prevQuadCtrlY = 0;
  let prevCmd = '';

  const num = (): number => tk[i++] as number;
  const peekIsNumber = (): boolean => typeof tk[i] === 'number';

  ctx.beginPath();

  while (i < tk.length) {
    let cmd = tk[i];
    if (typeof cmd === 'string') {
      i++;
    } else {
      // Implicit repeat: a bare coordinate set repeats the previous command (M→L).
      cmd = prevCmd === 'M' ? 'L' : prevCmd === 'm' ? 'l' : prevCmd;
    }
    const rel = cmd === (cmd as string).toLowerCase();
    const C = (cmd as string).toUpperCase();

    switch (C) {
      case 'M': {
        const x = num();
        const y = num();
        cx = rel ? cx + x : x;
        cy = rel ? cy + y : y;
        ctx.moveTo(cx, cy);
        startX = cx;
        startY = cy;
        break;
      }
      case 'L': {
        const x = num();
        const y = num();
        cx = rel ? cx + x : x;
        cy = rel ? cy + y : y;
        ctx.lineTo(cx, cy);
        break;
      }
      case 'H': {
        const x = num();
        cx = rel ? cx + x : x;
        ctx.lineTo(cx, cy);
        break;
      }
      case 'V': {
        const y = num();
        cy = rel ? cy + y : y;
        ctx.lineTo(cx, cy);
        break;
      }
      case 'C': {
        const x1 = (rel ? cx : 0) + num();
        const y1 = (rel ? cy : 0) + num();
        const x2 = (rel ? cx : 0) + num();
        const y2 = (rel ? cy : 0) + num();
        const x = (rel ? cx : 0) + num();
        const y = (rel ? cy : 0) + num();
        ctx.bezierCurveTo(x1, y1, x2, y2, x, y);
        prevCubicCtrlX = x2;
        prevCubicCtrlY = y2;
        cx = x;
        cy = y;
        break;
      }
      case 'S': {
        const reflect =
          prevCmd.toUpperCase() === 'C' || prevCmd.toUpperCase() === 'S';
        const x1 = reflect ? 2 * cx - prevCubicCtrlX : cx;
        const y1 = reflect ? 2 * cy - prevCubicCtrlY : cy;
        const x2 = (rel ? cx : 0) + num();
        const y2 = (rel ? cy : 0) + num();
        const x = (rel ? cx : 0) + num();
        const y = (rel ? cy : 0) + num();
        ctx.bezierCurveTo(x1, y1, x2, y2, x, y);
        prevCubicCtrlX = x2;
        prevCubicCtrlY = y2;
        cx = x;
        cy = y;
        break;
      }
      case 'Q': {
        const x1 = (rel ? cx : 0) + num();
        const y1 = (rel ? cy : 0) + num();
        const x = (rel ? cx : 0) + num();
        const y = (rel ? cy : 0) + num();
        ctx.quadraticCurveTo(x1, y1, x, y);
        prevQuadCtrlX = x1;
        prevQuadCtrlY = y1;
        cx = x;
        cy = y;
        break;
      }
      case 'T': {
        const reflect =
          prevCmd.toUpperCase() === 'Q' || prevCmd.toUpperCase() === 'T';
        const x1 = reflect ? 2 * cx - prevQuadCtrlX : cx;
        const y1 = reflect ? 2 * cy - prevQuadCtrlY : cy;
        const x = (rel ? cx : 0) + num();
        const y = (rel ? cy : 0) + num();
        ctx.quadraticCurveTo(x1, y1, x, y);
        prevQuadCtrlX = x1;
        prevQuadCtrlY = y1;
        cx = x;
        cy = y;
        break;
      }
      case 'A': {
        const rx = num();
        const ry = num();
        const rot = num();
        const large = num();
        const sweep = num();
        const x = (rel ? cx : 0) + num();
        const y = (rel ? cy : 0) + num();
        arcTo(ctx, cx, cy, rx, ry, rot, large !== 0, sweep !== 0, x, y);
        cx = x;
        cy = y;
        break;
      }
      case 'Z': {
        ctx.closePath();
        cx = startX;
        cy = startY;
        break;
      }
      default:
        throw new Error(`tracePathData: unsupported command "${cmd}"`);
    }

    prevCmd = cmd as string;
    // Guard against a malformed string spinning forever on a stuck index.
    if (!peekIsNumber() && typeof tk[i] !== 'string' && i < tk.length) {
      break;
    }
  }
}

/**
 * Replay an SVG elliptical-arc (`A`/`a`) command via `ctx.ellipse`, converting the
 * endpoint parameterization to centre parameterization (SVG spec impl notes F.6).
 * Lucide icon glyphs (e.g. CheckCircle) use arcs; the map's own shapes don't.
 */
function arcTo(
  ctx: PathTarget,
  x1: number,
  y1: number,
  rxIn: number,
  ryIn: number,
  rotDeg: number,
  largeArc: boolean,
  sweep: boolean,
  x2: number,
  y2: number,
): void {
  let rx = Math.abs(rxIn);
  let ry = Math.abs(ryIn);
  if (rx === 0 || ry === 0) {
    ctx.lineTo(x2, y2);

    return;
  }
  const phi = (rotDeg * Math.PI) / 180;
  const cosP = Math.cos(phi);
  const sinP = Math.sin(phi);
  const dx = (x1 - x2) / 2;
  const dy = (y1 - y2) / 2;
  const x1p = cosP * dx + sinP * dy;
  const y1p = -sinP * dx + cosP * dy;

  // Scale radii up if they're too small to span the endpoints.
  const lambda = (x1p * x1p) / (rx * rx) + (y1p * y1p) / (ry * ry);
  if (lambda > 1) {
    const s = Math.sqrt(lambda);
    rx *= s;
    ry *= s;
  }

  const sign = largeArc !== sweep ? 1 : -1;
  const num = rx * rx * ry * ry - rx * rx * y1p * y1p - ry * ry * x1p * x1p;
  const den = rx * rx * y1p * y1p + ry * ry * x1p * x1p;
  const co = sign * Math.sqrt(Math.max(0, num / den));
  const cxp = (co * (rx * y1p)) / ry;
  const cyp = (-co * (ry * x1p)) / rx;
  const cx = cosP * cxp - sinP * cyp + (x1 + x2) / 2;
  const cy = sinP * cxp + cosP * cyp + (y1 + y2) / 2;

  const angle = (ux: number, uy: number, vx: number, vy: number): number => {
    const dot = ux * vx + uy * vy;
    const len = Math.sqrt((ux * ux + uy * uy) * (vx * vx + vy * vy));
    let a = Math.acos(Math.max(-1, Math.min(1, dot / len)));
    if (ux * vy - uy * vx < 0) {
      a = -a;
    }

    return a;
  };

  const theta1 = angle(1, 0, (x1p - cxp) / rx, (y1p - cyp) / ry);
  let dTheta = angle(
    (x1p - cxp) / rx,
    (y1p - cyp) / ry,
    (-x1p - cxp) / rx,
    (-y1p - cyp) / ry,
  );
  if (!sweep && dTheta > 0) {
    dTheta -= 2 * Math.PI;
  } else if (sweep && dTheta < 0) {
    dTheta += 2 * Math.PI;
  }

  ctx.ellipse(cx, cy, rx, ry, phi, theta1, theta1 + dTheta, !sweep);
}

/** Read the SVG source `d` stashed on a `Path2D` by `draw/shapes.ts`, if present. */
export function pathDataOf(path: Path2D, key: symbol): string | undefined {
  return (path as unknown as Record<symbol, string | undefined>)[key];
}
